import { type Rating, type TargetRating, TargetRatingDirection } from 'connect-graphql/generated';
import { findIndex } from 'lodash-es';

import { logger } from '@/src/internal/logger';

type FlattenedTargetRating = { readonly rating?: never; readonly ratingId: string } & Pick<
  TargetRating,
  'direction'
>;

type DeepTargetRating = {
  readonly rating: { readonly id: string };
  readonly ratingId?: never;
} & Pick<TargetRating, 'direction'>;

interface RatingSatisfiesTargetRatingParams {
  /**
   * The overall set of ratings, {@link Pick<Rating, 'id' | 'order'>[]}, that are used to determine
   * the relative locations of the {@link Rating} associated with the {@link TargetRating} and
   * the {@link Rating} associated with the provided ID.
   */
  readonly ratings: Pick<Rating, 'id' | 'order'>[];
  readonly targetRating: DeepTargetRating | FlattenedTargetRating;
}

/**
 * Determines if the {@link Rating} with the provided ID satisfies the provided {@link TargetRating}
 * based on the relative locations of the {@link Rating} (associated with the provided ID) and the
 * {@link Rating} (associated with the {@link TargetRating}) in the set of provided ratings,
 * {@link Rating[]} and the direction of the {@link TargetRating}.
 *
 * @param {string} ratingId
 *   The ID of the rating, {@link Rating}, for which the determination as to whether or not it
 *   satisfies the target rating, {@link TargetRating}, should be made.  The provided 'ratingId'
 *   must correspond to a rating in the set of provided ratings, {@link Rating[]} - if it does not,
 *   an error will be logged.
 * @param {RatingSatisfiesTargetRatingParams} params
 *   Parameters for the method that include the target rating, {@link TargetRating}, and the overall
 *   set of ratings, {@link Rating[]}.
 *
 * @returns {boolean}
 *   Whether or not the {@link Rating} with the provided ID satisfies the provided target rating,
 *   {@link TargetRating}.
 */
export const ratingSatisfiesTargetRating = (
  ratingId: string,
  { ratings, targetRating }: RatingSatisfiesTargetRatingParams,
) => {
  const targetRatingRatingId = targetRating.rating ? targetRating.rating.id : targetRating.ratingId;
  const sortedRatings = ratings.sort((a, b) => a.order - b.order);

  const targetRatingIndex = findIndex(sortedRatings, rat => rat.id === targetRatingRatingId);
  if (targetRatingIndex === -1) {
    logger.model.error(
      'When determining whether or not a given rating satisfies a target rating, the target ' +
        `rating's rating ID, '${targetRatingRatingId}', was not found in the set of provided ` +
        'ratings. The assumption will be made that the rating does not satisfy the target rating.',
    );
    return false;
  }

  const ratingIndex = findIndex(sortedRatings, rat => rat.id === ratingId);
  if (ratingIndex === -1) {
    logger.model.error(
      'When determining whether or not a given rating satisfies a target rating, the rating ID, ' +
        `'${ratingId}', was not found in the set of provided ratings. The assumption will be ` +
        'made that the rating does not satisfy the target rating.',
    );
    return false;
  }

  const SatisfiedMap: Record<TargetRatingDirection, boolean> = {
    [TargetRatingDirection.Left]: ratingIndex <= targetRatingIndex,
    [TargetRatingDirection.Right]: ratingIndex >= targetRatingIndex,
  };

  return SatisfiedMap[targetRating.direction];
};
